import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { PublicLayout } from './layouts';
import './haulercentral-theme.css';
import {
  GuestGuard,
  HaulerCentralAuthGuard,
  HaulerCentralAuthProvider,
  storeAuth,
  useHaulerCentralAuthContext,
  useHaulerCentralAuthStore,
} from '@/lib';

// Auth pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ResendVerificationPage = lazy(() => import('./pages/ResendVerificationPage'));

// Main pages
const HomePage = lazy(() => import('./pages/HomePage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ClaimProfilePage = lazy(() => import('./pages/ClaimProfilePage'));
const RFQListPage = lazy(() => import('./pages/RFQListPage'));
const RFQDetailPage = lazy(() => import('./pages/RFQDetailPage'));
const RFQCreatePage = lazy(() => import('./pages/RFQCreatePage'));
const RFQEditPage = lazy(() => import('./pages/RFQEditPage'));
const PriceCheckPage = lazy(() => import('./pages/PriceCheckPage'));
const PriceMatrixPage = lazy(() => import('./pages/PriceMatrixPage'));

// Profile management pages (moved from shell)
const HaulerCentralProfilePage = lazy(
  () => import('./features/profile-management/pages/HaulerCentralProfilePage')
);
const HaulerCentralProfileWizardPage = lazy(
  () => import('./features/profile-management/pages/HaulerCentralProfileWizardPage')
);
const ReceivedRFQListPage = lazy(
  () => import('./features/profile-management/pages/ReceivedRFQListPage')
);
const ReceivedRFQDetailPage = lazy(
  () => import('./features/profile-management/pages/ReceivedRFQDetailPage')
);
const QuotesListPage = lazy(() => import('./features/profile-management/pages/QuotesListPage'));
const QuotesDetailPage = lazy(() => import('./features/profile-management/pages/QuotesDetailPage'));

// Profile creation request pages
const ProfileRequestPage = lazy(() => import('./pages/ProfileRequestPage'));
const ProfileRequestsPage = lazy(() => import('./pages/ProfileRequestsPage'));

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

/**
 * OAuth callback handler component
 * Processes OAuth redirect from backend (tokens now in secure cookies)
 * P1 Security: Tokens are in httpOnly cookies, not in URL params
 */
function OAuthCallbackHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const processCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const authStatus = params.get('auth');

        // Verify OAuth was successful
        if (authStatus !== 'success') {
          navigate('../login?error=oauth_failed', { replace: true });
          return;
        }

        // Exchange the httpOnly OAuth cookie for tokens in the response body
        // so we can store them in localStorage for subsequent API calls.
        const apiOriginFromCallback = params.get('api_origin');
        const API_BASE_URL = (apiOriginFromCallback || import.meta.env.VITE_API_BASE_URL).replace(
          /\/+$/,
          ''
        );
        const exchangeRes = await fetch(`${API_BASE_URL}/haulercentral/auth/oauth/exchange`, {
          method: 'GET',
          credentials: 'include', // send the httpOnly cookie
        });

        if (!exchangeRes.ok) {
          navigate('../login?error=oauth_failed', { replace: true });
          return;
        }

        const exchangeData = await exchangeRes.json();

        if (!exchangeData.success || !exchangeData.data) {
          navigate('../login?error=oauth_failed', { replace: true });
          return;
        }

        const { accessToken, refreshToken, user } = exchangeData.data;

        // Store tokens + user the same way normal login does,
        // and update the Zustand store so isAuthenticated becomes true.
        storeAuth({ accessToken, refreshToken: refreshToken || '' }, user);
        const authStore = useHaulerCentralAuthStore.getState();
        authStore.setUser(user);
        authStore.setIsAuthenticated(true);
        window.dispatchEvent(new CustomEvent('auth:login', { detail: { user } }));

        // Get the return URL from state
        const stateParam = params.get('state');
        let returnTo = '/dashboard';

        if (stateParam) {
          try {
            const state = JSON.parse(stateParam);
            returnTo = state.returnTo || returnTo;
          } catch {
            // Invalid state, use default
          }
        }

        navigate(returnTo, { replace: true });
      } catch (error) {
        console.error('OAuth callback error:', error);
        navigate('../login?error=oauth_failed', { replace: true });
      }
    };

    processCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}

function DashboardRedirect() {
  useEffect(() => {
    const haulerCentralUrl =
      import.meta.env.VITE_HAULERCENTRAL_URL ||
      import.meta.env.VITE_HAULERCENTRAL_URL ||
      'http://localhost:3005';
    window.location.href = haulerCentralUrl;
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { isLoading } = useHaulerCentralAuthContext();

  // Show loading while checking auth state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="haulercentral-theme">
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Auth routes (standalone pages) - redirect if already authenticated */}
          {/* Auth routes use relative paths for standalone routing */}
          <Route
            path="login"
            element={
              <GuestGuard redirectTo="/">
                <LoginPage />
              </GuestGuard>
            }
          />
          <Route
            path="register"
            element={
              <GuestGuard redirectTo="/">
                <RegisterPage />
              </GuestGuard>
            }
          />
          <Route
            path="forgot-password"
            element={
              <GuestGuard redirectTo="/">
                <ForgotPasswordPage />
              </GuestGuard>
            }
          />
          <Route
            path="reset-password"
            element={
              <GuestGuard redirectTo="/">
                <ResetPasswordPage />
              </GuestGuard>
            }
          />
          <Route
            path="resend-verification"
            element={
              <GuestGuard redirectTo="/">
                <ResendVerificationPage />
              </GuestGuard>
            }
          />

          {/* OAuth callback route */}
          <Route path="oauth/callback" element={<OAuthCallbackHandler />} />

          {/* Email verification route - accessible with or without auth */}
          <Route path="verify-email" element={<VerifyEmailPage />} />

          {/* Public routes - accessible without authentication */}
          <Route element={<PublicLayout />}>
            <Route index element={<HomePage />} />
            <Route path="dashboard" element={<DashboardRedirect />} />
            <Route path="search" element={<SearchResultsPage />} />
            <Route path="haulers/:id" element={<ProfilePage />} />
            <Route path="claim/:id" element={<ClaimProfilePage />} />
            <Route path="rfq" element={<RFQListPage />} />
            <Route path="rfq/create" element={<RFQCreatePage />} />
            <Route path="rfq/:id" element={<RFQDetailPage />} />
            <Route path="rfq/:id/edit" element={<RFQEditPage />} />
            <Route path="price-check" element={<PriceCheckPage />} />
            <Route path="pricing" element={<PriceMatrixPage />} />
          </Route>

          {/* Authenticated profile management routes */}
          <Route element={<PublicLayout />}>
            <Route
              path="profile/manage"
              element={
                <HaulerCentralAuthGuard>
                  <HaulerCentralProfilePage />
                </HaulerCentralAuthGuard>
              }
            />
            <Route
              path="profile/setup"
              element={
                <HaulerCentralAuthGuard>
                  <HaulerCentralProfileWizardPage />
                </HaulerCentralAuthGuard>
              }
            />
            <Route
              path="profile/rfqs"
              element={
                <HaulerCentralAuthGuard>
                  <ReceivedRFQListPage />
                </HaulerCentralAuthGuard>
              }
            />
            <Route
              path="profile/rfqs/:id"
              element={
                <HaulerCentralAuthGuard>
                  <ReceivedRFQDetailPage />
                </HaulerCentralAuthGuard>
              }
            />
            <Route
              path="profile/quotes"
              element={
                <HaulerCentralAuthGuard>
                  <QuotesListPage />
                </HaulerCentralAuthGuard>
              }
            />
            <Route
              path="profile/quotes/:id"
              element={
                <HaulerCentralAuthGuard>
                  <QuotesDetailPage />
                </HaulerCentralAuthGuard>
              }
            />
            <Route
              path="profile/request"
              element={
                <HaulerCentralAuthGuard>
                  <ProfileRequestPage />
                </HaulerCentralAuthGuard>
              }
            />
            <Route
              path="profile/requests"
              element={
                <HaulerCentralAuthGuard>
                  <ProfileRequestsPage />
                </HaulerCentralAuthGuard>
              }
            />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <HaulerCentralAuthProvider>
      <AppContent />
    </HaulerCentralAuthProvider>
  );
}

export default App;
